import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import {
  JADE,
  JADE_DEEP,
  OBSIDIAN,
  SURFACE,
  LINE,
  MUTED,
  TEXT_LIGHT,
  TEXT_DARK,
  MUTED_COOL,
  PEARL,
  WHITE,
  LIGHT_BORDER,
} from '../../theme/brand';

// Palette wiring for MUI, kept in sync with app/src/theme/brand.js.
// See REBRAND_PLAN_Jade.md §4 for the surface→token pairing reference.
const MuiDarkModeProvider = ({ children }) => {
  const darkMode = useSelector((state) => state.palettes.darkMode);

  const theme = useMemo(
    () =>
      createTheme({
        // Follow the app's rem sizing, including its compact desktop density.
        // At the standard 16px root this remains MUI's usual 8px spacing unit.
        spacing: (factor) => typeof factor === 'string' ? factor : `${factor * 0.5}rem`,
        components: {
          MuiDialog: {
            styleOverrides: {
              paper: ({ ownerState }) => ownerState.fullScreen ? {} : ({
                '@media (max-width: 767px)': {
                  margin: 12,
                  maxWidth: 'calc(100% - 24px)',
                  maxHeight: 'calc(100dvh - 24px)',
                },
              }),
            },
          },
          MuiDialogActions: {
            styleOverrides: { root: { flexWrap: 'wrap', gap: 8 } },
          },
        },
        palette: {
          mode: darkMode ? 'dark' : 'light',
          primary: {
            // Dark mode uses brand jade #00A86B + white text. Light mode uses the
            // lighter "emerald-600" jade (#059669) to match the Tailwind primary
            // buttons. dark (#006B45) is the hover/darken shade in both modes.
            main: darkMode ? JADE : '#059669',
            light: JADE,
            dark: JADE_DEEP,
            contrastText: WHITE,
          },
          ...(darkMode
            ? {
                background: {
                  default: OBSIDIAN,
                  paper: SURFACE,
                },
                text: {
                  primary: TEXT_LIGHT,
                  secondary: MUTED,
                },
                divider: LINE,
              }
            : {
                background: {
                  default: PEARL,
                  paper: WHITE,
                },
                text: {
                  primary: TEXT_DARK,
                  secondary: MUTED_COOL,
                },
                divider: LIGHT_BORDER,
              }),
        },
      }),
    [darkMode],
  );

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};

export default MuiDarkModeProvider;
